'use client';

import { Button } from 'flowbite-react';
import { HiCalendar, HiChatAlt2, HiMail, HiPhone } from 'react-icons/hi';
import { Locale } from '../../../../i18n-config';
import { useTranslation } from '@/i18n/client';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import ProgressiveForm from './ProgressiveForm';
import QuickContactModal from './QuickContactModal';
import SectionLabel from '../home/SectionLabel';
import React, { useState } from 'react';

type Props = {
  lang: Locale;
};

type ContactMethod = 'whatsapp' | 'quick' | 'detailed' | 'demo' | null;

const ContactMethodSelector = ({ lang }: Props) => {
  const { t } = useTranslation(lang);
  const [selectedMethod, setSelectedMethod] = useState<ContactMethod>(null);
  const [showQuickModal, setShowQuickModal] = useState(false);
  const [showProgressiveForm, setShowProgressiveForm] = useState(false);

  const handleWhatsAppContact = () => {
    const message = encodeURIComponent(t('ContactUs/WhatsApp/default_message'));
    window.open(`https://wa.me/41783145598?text=${message}`, '_blank');
  };

  const handleDemoRequest = () => {
    const demoUrl = lang.startsWith('fr')
      ? 'https://tidycal.com/pinkswan/super-user-manager-fr'
      : 'https://tidycal.com/pinkswan/super-user-manager';
    window.open(demoUrl, '_blank');
  };

  const contactMethods = [
    {
      id: 'whatsapp' as const,
      title: t('ContactUs/Methods/whatsapp_title'),
      description: t('ContactUs/Methods/whatsapp_description'),
      icon: HiChatAlt2,
      action: handleWhatsAppContact,
      badge: t('ContactUs/V3/Methods/whatsapp_badge'),
    },
    {
      id: 'quick' as const,
      title: t('ContactUs/Methods/quick_title'),
      description: t('ContactUs/Methods/quick_description'),
      icon: HiMail,
      action: () => setShowQuickModal(true),
      badge: t('ContactUs/V3/Methods/quick_badge'),
    },
    {
      id: 'demo' as const,
      title: t('ContactUs/Methods/demo_title'),
      description: t('ContactUs/Methods/demo_description'),
      icon: HiCalendar,
      action: handleDemoRequest,
      badge: t('ContactUs/Methods/free'),
    },
    {
      id: 'detailed' as const,
      title: t('ContactUs/Methods/detailed_title'),
      description: t('ContactUs/Methods/detailed_description'),
      icon: HiPhone,
      action: () => setShowProgressiveForm(true),
      badge: t('ContactUs/Methods/comprehensive'),
    },
  ];

  if (showProgressiveForm) {
    return (
      <section className="px-6 py-24 lg:py-28" style={{ background: '#0E1130', color: '#F5F7FA' }}>
        <div className="mx-auto max-w-[1200px]">
          <SectionLabel num="02" label={t('ContactUs/V3/Progressive/label')} />
          <h2
            className="font-bold tracking-tight"
            style={{
              fontSize: 'clamp(24px, 2.8vw, 36px)',
              lineHeight: 1.15,
              letterSpacing: '-0.01em',
              color: '#F5F7FA',
              margin: '14px 0 14px',
              maxWidth: 720,
            }}
          >
            {t('ContactUs/V3/Progressive/headline_a')}{' '}
            <span style={{ color: '#B4C1D8' }}>{t('ContactUs/V3/Progressive/headline_b')}</span>
          </h2>
          <p style={{ color: '#B4C1D8', fontSize: 16, lineHeight: 1.5, maxWidth: 640, margin: '0 0 36px' }}>
            {t('ContactUs/V3/Progressive/subhead')}
          </p>
          <ErrorBoundary>
            <ProgressiveForm lang={lang} onBack={() => setShowProgressiveForm(false)} />
          </ErrorBoundary>
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 py-24 lg:py-28" style={{ background: '#0E1130', color: '#F5F7FA' }}>
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num="02" label={t('ContactUs/V3/Methods/label')} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(24px, 2.8vw, 36px)',
            lineHeight: 1.15,
            letterSpacing: '-0.01em',
            color: '#F5F7FA',
            margin: '14px 0 14px',
            maxWidth: 720,
          }}
        >
          {t('ContactUs/V3/Methods/headline_a')}{' '}
          <span style={{ color: '#B4C1D8' }}>{t('ContactUs/V3/Methods/headline_b')}</span>
        </h2>
        <p style={{ color: '#B4C1D8', fontSize: 16, lineHeight: 1.5, maxWidth: 640, margin: '0 0 36px' }}>
          {t('ContactUs/V3/Methods/subhead')}
        </p>

        <div className="grid md:grid-cols-2 gap-4 mb-12">
          {contactMethods.map((method) => {
            const IconComponent = method.icon;
            return (
              <div
                key={method.id}
                style={{
                  position: 'relative',
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 18,
                  padding: '22px 22px 24px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                  transition: 'background 0.2s, border-color 0.2s',
                }}
              >
                {method.badge && (
                  <span
                    className="mono"
                    style={{
                      position: 'absolute',
                      top: 14,
                      right: 14,
                      fontSize: 10,
                      letterSpacing: 1.5,
                      textTransform: 'uppercase',
                      color: '#22D3EE',
                      background: 'rgba(34,211,238,0.1)',
                      border: '1px solid rgba(34,211,238,0.3)',
                      padding: '3px 8px',
                      borderRadius: 999,
                    }}
                  >
                    {method.badge}
                  </span>
                )}

                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    background: 'rgba(34,211,238,0.08)',
                    border: '1px solid rgba(34,211,238,0.18)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <IconComponent style={{ width: 22, height: 22, color: '#22D3EE' }} />
                </div>

                <div>
                  <h3
                    style={{
                      fontSize: 19,
                      fontWeight: 600,
                      letterSpacing: '-0.01em',
                      color: '#F5F7FA',
                      margin: '0 0 6px',
                    }}
                  >
                    {method.title}
                  </h3>
                  <p style={{ color: '#B4C1D8', fontSize: 14, lineHeight: 1.5, margin: 0 }}>{method.description}</p>
                </div>

                <Button
                  onClick={method.action}
                  className="sum-cta-ghost"
                  style={{ alignSelf: 'flex-start', marginTop: 4 }}
                >
                  {method.id === 'whatsapp'
                    ? t('ContactUs/Methods/chat_now')
                    : method.id === 'quick'
                    ? t('ContactUs/Methods/send_message')
                    : method.id === 'demo'
                    ? t('ContactUs/Methods/book_demo')
                    : t('ContactUs/Methods/start_form')}{' '}
                  →
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Contact Modal */}
      <ErrorBoundary>
        <QuickContactModal lang={lang} show={showQuickModal} onClose={() => setShowQuickModal(false)} />
      </ErrorBoundary>
    </section>
  );
};

export default ContactMethodSelector;
